import DividerWithText from '@frontend/components/DividerWithText'
import { Checkbox, Input, PasswordInput } from '@frontend/components/form'
import { Button } from '@nextui-org/react'
import { INPUT_VARIANT } from '@shared/constant'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

import type { LoginState } from './lib/type'

import { useLogin } from './lib/hook'
import SocialLogin from './SocialLogin'

export function SignInForm() {
  const { control, handleSubmit } = useForm<LoginState>({
    defaultValues: {
      password: '',
      rememberMe: false,
      username: '',
    },
    mode: 'onTouched',
  })

  const { isLoading, mutate } = useLogin()

  const onSubmit = useCallback(
    async (data: LoginState) => {
      await toast.promise(mutate(data), {
        error: (error) => error?.message || 'Failed to forget password',
        loading: 'Logging in...',
        success: (response) => {
          window.location.reload()
          return response?.message || 'Forgot password successfully'
        },
      })
    },
    [mutate],
  )

  return (
    <>
      <h2 className="mb-10 text-center font-medium uppercase">
        Already have an account?
      </h2>
      <form className="relative space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <Input
          control={control}
          fieldProps={{
            autoComplete: 'username',
            label: 'Username or Email Address',
            variant: INPUT_VARIANT,
          }}
          name="username"
          rules={{
            required: 'Please enter your username or email address',
          }}
        />
        <PasswordInput
          control={control}
          fieldProps={{
            autoComplete: 'current-password',
            label: 'Password',
            variant: INPUT_VARIANT,
          }}
          name="password"
          rules={{ required: 'Please enter your password' }}
        />
        <div className="flex justify-between">
          <Checkbox
            control={control}
            fieldProps={{
              classNames: {
                label: 'uppercase text-xs',
              },
            }}
            label="Remember me"
            name="rememberMe"
          />
          <Link
            className="text-xs uppercase text-primary"
            to="/my-account/lost-password"
          >
            Forget your password?
          </Link>
        </div>
        <Button
          className="!mt-10 font-medium"
          color="primary"
          fullWidth
          isLoading={isLoading}
          size="lg"
          type="submit"
        >
          Sign in
        </Button>
      </form>
      <DividerWithText className="my-8" text="OR" />
      <SocialLogin
        facebookButtonText="Sign in with Facebook"
        googleButtonText="Sign in with Google"
      />

      <p className="mt-16 block text-center text-sm uppercase text-gray-600">
        Don&apos;t have an account?{' '}
        <Link className="text-primary" to="/my-account/">
          Sign Up
        </Link>
      </p>

      <small className="mt-10 block text-center text-gray-600">
        By click &quot;Sign in&quot;, you agree to our{' '}
        <Link className="text-primary" to="/term-conditions">
          Terms and Conditions
        </Link>{' '}
        and{' '}
        <Link className="text-primary" to="/privacy-policy">
          Privacy Policy
        </Link>
        .
      </small>
    </>
  )
}

export default SignInForm
